import React from "react";

export const MANAGED_HEAD_ATTR = "data-head";

const DYNAMIC_HEAD_SELECTORS = [
  "title",
  'meta[name="description"]',
  'meta[property="og:type"]',
  'meta[property="og:url"]',
  'meta[property="og:site_name"]',
  'meta[property="og:locale"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'link[rel="canonical"]',
];

export interface HeadTagsConfig {
  title: string;
  description: string;
  pageUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl: string;
  canonicalUrl?: string;
}

export type HeadTagDescriptor =
  | { type: "title"; text: string }
  | { type: "meta"; name?: string; property?: string; content: string }
  | { type: "link"; rel: string; href: string };

export function buildHeadTagDescriptors(
  config: HeadTagsConfig,
): HeadTagDescriptor[] {
  const tags: HeadTagDescriptor[] = [
    { type: "title", text: config.title },
    { type: "meta", name: "description", content: config.description },
    { type: "meta", property: "og:type", content: "website" },
    { type: "meta", property: "og:url", content: config.pageUrl },
    {
      type: "meta",
      property: "og:site_name",
      content: config.title,
    },
    { type: "meta", property: "og:locale", content: "he_IL" },
    { type: "meta", property: "og:title", content: config.ogTitle },
    { type: "meta", property: "og:description", content: config.ogDescription },
    { type: "meta", property: "og:image", content: config.ogImageUrl },
    { type: 'meta', property: 'twitter:card', content: 'summary_large_image' },
    { type: 'meta', property: "og:image:width", content: '1200' },
    { type: 'meta', property: "og:image:height", content: '630' },
    { type: 'meta', property: 'twitter:image', content: config.ogImageUrl },
  ];

  if (config.canonicalUrl) {
    tags.push({ type: "link", rel: "canonical", href: config.canonicalUrl });
  }

  return tags;
}

function createManagedElement(tagName: string): HTMLElement {
  const element = document.createElement(tagName);
  element.setAttribute(MANAGED_HEAD_ATTR, "true");

  return element;
}

function removeDynamicHeadTags(head: HTMLHeadElement) {
  DYNAMIC_HEAD_SELECTORS.forEach((selector) => {
    head.querySelectorAll(selector).forEach((element) => {
      if (!element.hasAttribute(MANAGED_HEAD_ATTR)) {
        element.remove();
      }
    });
  });
}

function appendDescriptor(
  head: HTMLHeadElement,
  descriptor: HeadTagDescriptor,
) {
  if (descriptor.type === "title") {
    const title = createManagedElement("title");
    title.textContent = descriptor.text;
    head.appendChild(title);
    return;
  }

  if (descriptor.type === "meta") {
    const meta = createManagedElement("meta") as HTMLMetaElement;
    if (descriptor.name) meta.setAttribute("name", descriptor.name);
    if (descriptor.property) meta.setAttribute("property", descriptor.property);
    meta.setAttribute("content", descriptor.content);
    head.appendChild(meta);
    return;
  }

  const link = createManagedElement("link") as HTMLLinkElement;
  link.setAttribute("rel", descriptor.rel);
  link.setAttribute("href", descriptor.href);
  head.appendChild(link);
}

export function applyHeadTags(descriptors: HeadTagDescriptor[]): () => void {
  const head = document.head;

  head
    .querySelectorAll(`[${MANAGED_HEAD_ATTR}]`)
    .forEach((element) => element.remove());
  removeDynamicHeadTags(head);

  descriptors.forEach((descriptor) => appendDescriptor(head, descriptor));

  return () => {
    head
      .querySelectorAll(`[${MANAGED_HEAD_ATTR}]`)
      .forEach((element) => element.remove());
  };
}

export function headTagsToReactElements(
  descriptors: HeadTagDescriptor[],
): React.ReactNode {
  return descriptors.map((descriptor, index) => {
    if (descriptor.type === "title") {
      return React.createElement(
        "title",
        { key: `title-${index}` },
        descriptor.text,
      );
    }

    if (descriptor.type === "meta") {
      return React.createElement("meta", {
        key: `meta-${index}`,
        name: descriptor.name,
        property: descriptor.property,
        content: descriptor.content,
      });
    }

    return React.createElement("link", {
      key: `link-${index}`,
      rel: descriptor.rel,
      href: descriptor.href,
    });
  });
}
